import cc from 'classcat';
import { useRef, useState, useId, useEffect } from 'react';
import { FormulaIcon } from '@i-ark/components';
import { InputTypes } from '@i-ark/models';
import { preventDefault } from '@i-ark/utils';
import { breakpoints } from '@ui/css';
import { PencilIcon } from '@ui/kit/icons';
import { useIsTouchDevice } from '@utils/common';
import { useComponentEntry } from '@utils/component-framework';
import { useMatchBreakpoint } from '@utils/react';
import { useChangeInputEntryMutation } from '../../../state/mutations/entries/change.input.js';
import '@tanstack/react-query';
import { selectLastComponentEditorVersion } from '../../../state/selectors/data.last-editor-version.js';
import { selectItemInputEntryById } from '../../../state/selectors/entry.item-input-by-id.js';
import { selectItemInputEntryExists } from '../../../state/selectors/entry.item-input-exists.js';
import { selectOriginalComponentVersion } from '../../../state/selectors/data.original-version.js';
import { iArkStore } from '../../../state/store.js';
import { useRichTextEditor, getRichTextEditor } from '../hooks/useRichTextEditor.js';
import { useSheetScale } from '../hooks/useSheetScale.js';
import { placeholderIconCls, inputCls, formulaEditorCls, containerCls } from './RichTextInput.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const RichTextInput = ({
  pageId,
  item,
  sheetRef
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const maxHeight = useRef(undefined);
  const [disableOverflow, setDisableOverflow] = useState(false);
  const isTouchDevice = useIsTouchDevice();
  const isMobile = useMatchBreakpoint(breakpoints.mobile.andDown());
  const scale = useSheetScale(sheetRef);
  const lastEditorVersion = iArkStore.useSelector(selectLastComponentEditorVersion);
  const editorId = useId();
  const [isFocused, setFocused] = useState(false);
  const fontScaleRef = useRef(1);
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const originalVersion = iArkStore.useSelector(selectOriginalComponentVersion);
  const enableFormulaEditor = iArkStore.useSelector(state => state.component.data.settings.enabledFeatures.wiris) && !isTouchDevice && !isMobile && (originalVersion === 2 || item.type !== InputTypes.INPUT);
  const changeInputEntryMutation = useChangeInputEntryMutation(entryKey);
  const itemEntry = useComponentEntry(entryKey, {
    select: selectItemInputEntryById(pageId, item.id)
  });
  const itemEntryExists = useComponentEntry(entryKey, {
    select: selectItemInputEntryExists(pageId, item.id)
  });
  const zoom = iArkStore.useSelector(state => state.zoom);
  const prevEntry = useRef(itemEntry.data?.value ?? "");
  const handleChange = value => {
    const editor = getRichTextEditor(editorId);
    const container = containerRef.current;
    const input = container?.querySelector(".fr-view") ?? container;
    if (editor === undefined || container === null) {
      return;
    }
    if (maxHeight.current === undefined) {
      updateMaxHeight({
        maxHeight,
        editor,
        container
      });
    }
    if (maxHeight.current !== undefined) {
      container.style.overflow = "hidden";
      if (lastEditorVersion === 1) {
        // iteratively scale font, until we can just fit the entire text content
        // (or reach the predefined maximum/minimum font size)
        while (container.scrollHeight <= maxHeight.current && fontScaleRef.current < 0.95) {
          fontScaleRef.current += 0.1;
          container.style.fontSize = `${fontScaleRef.current}em`;
        }
        while (container.scrollHeight > maxHeight.current && fontScaleRef.current > 0.15) {
          fontScaleRef.current -= 0.1;
          container.style.fontSize = `${fontScaleRef.current}em`;
        }
      }

      // if we still cannot fit the available text, then also crop the entry so
      // scrollbars are never added to the field
      if (item.type === InputTypes.TEXTAREA ? container.scrollHeight > maxHeight.current : input && input.scrollWidth > input.clientWidth && editor.html.get() !== "") {
        // reset change if content has become taller than the container, as we
        // will not accept scrolling
        editor.el.innerHTML = prevEntry.current;
        if (prevEntry.current.length > 0) {
          // set cursor at the end of editor when resetting content
          const range = document.createRange();
          range.setStart(editor.el, editor.el.childNodes.length);
          range.collapse(true);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        container.style.overflow = "";
        return;
      }
      container.style.overflow = "";
    }

    // cache previous value in case we need to reset to prevent scrolling later
    // on
    prevEntry.current = value;
    changeInputEntryMutation({
      pageId,
      itemId: item.id,
      value
    });
  };
  const placeholder = item.settings.usePlaceholderAsDefaultValue !== true ? item.placeholder : "";
  const value = item.settings.usePlaceholderAsDefaultValue === true && !itemEntryExists.data ? item.placeholder.replace(/\n/g, "<br />") : itemEntry.data?.value ?? "";
  useRichTextEditor({
    editorId,
    entryKey,
    ref: inputRef,
    placeholder,
    value,
    maxLength: item.maxLength,
    onChange: handleChange,
    onReady: () => {
      const editor = getRichTextEditor(editorId);
      const container = containerRef.current;
      if (editor === undefined || container === null) {
        return;
      }
      updateMaxHeight({
        maxHeight,
        editor,
        container
      });
      if (maxHeight.current !== undefined && lastEditorVersion === 1) {
        // iteratively scale font, until we can just fit the entire text content
        // (or reach the predefined maximum/minimum font size)
        while (container.scrollHeight > maxHeight.current && fontScaleRef.current > 0.15) {
          fontScaleRef.current -= 0.1;
          container.style.fontSize = `${fontScaleRef.current}em`;
        }
      }
    },
    onFocus: () => {
      setFocused(true);
      const editor = getRichTextEditor(editorId);
      const container = containerRef.current;
      if (editor === undefined || container === null) {
        return;
      }
      updateMaxHeight({
        maxHeight,
        editor,
        container
      });
    },
    onBlur: () => {
      setFocused(false);
    }
  });

  // auto-udpate max height whenever zoom level is changed
  useEffect(() => {
    const editor = getRichTextEditor(editorId);
    const container = containerRef.current;
    if (editor === undefined || container === null) {
      return;
    }
    updateMaxHeight({
      maxHeight,
      editor,
      container
    });
    requestAnimationFrame(() => {
      editor.placeholder?.refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, scale]);

  // disable scrollbars completely if not needed initially (we'll never allow
  // the user to enter a state where the scrollbars appears afterwards anyway)
  useEffect(() => {
    const editor = getRichTextEditor(editorId);
    const container = containerRef.current;
    if (editor === undefined || container === null) {
      return;
    }
    updateMaxHeight({
      maxHeight,
      editor,
      container
    });
    setDisableOverflow(maxHeight.current !== undefined && container.scrollHeight <= maxHeight.current);
  }, [editorId, entryKey]);
  const handleFormulaEditorClick = () => {
    const editor = getRichTextEditor(editorId);
    if (editor === undefined) {
      return;
    }
    editor.geogebra.open();
  };
  const handleFormulaEditorMouseDown = preventDefault;
  const handleFormulaEditorTouchStart = preventDefault;
  return /*#__PURE__*/jsxs("div", {
    ref: containerRef,
    className: cc([containerCls, {
      verticallyCenter: item.type === InputTypes.INPUT,
      disableOverflow: disableOverflow,
      singleLine: item.type === InputTypes.INPUT,
      usePlaceholderAsDefaultValue: item.settings.usePlaceholderAsDefaultValue,
      showPencil: item.settings.usePlaceholderAsDefaultValue ? !itemEntryExists.data || (value ?? "").length === 0 : (value ?? "").length === 0
    }]),
    style: {
      fontSize: `${fontScaleRef.current}em`
    },
    children: [/*#__PURE__*/jsx(PencilIcon, {
      className: cc([placeholderIconCls, "pencil"])
    }), /*#__PURE__*/jsx("textarea", {
      ref: inputRef,
      className: inputCls,
      placeholder: placeholder,
      defaultValue: value
    }), enableFormulaEditor && /*#__PURE__*/jsx("button", {
      className: cc([formulaEditorCls, {
        focused: isFocused
      }]),
      onClick: handleFormulaEditorClick,
      onMouseDown: handleFormulaEditorMouseDown,
      onTouchStart: handleFormulaEditorTouchStart,
      children: /*#__PURE__*/jsx(FormulaIcon, {
        size: 12
      })
    })]
  });
};
function updateMaxHeight({
  maxHeight,
  editor,
  container
}) {
  if (!editor.el) {
    return;
  }
  editor.el.style.lineHeight = "0";
  maxHeight.current = Math.max(container.clientHeight, container.scrollHeight);
  editor.el.style.lineHeight = "";
}

export { RichTextInput };
//# sourceMappingURL=RichTextInput.js.map
